import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import Layout, { PRIMARY_CTA, BookingLink } from "../components/Layout";
import SEO from "../components/SEO";
import { api, track, errorText } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [state, setState] = useState({ loading: false, error: "", done: false });

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: "", done: false });
    try {
      await api.post("/contact/message", form);
      track("contact_message", "/contact");
      setState({ loading: false, error: "", done: true });
    } catch (err) {
      setState({ loading: false, error: errorText(err, "Could not send. Please check your details and try again."), done: false });
    }
  };

  return (
    <Layout>
      <SEO title="Contact Driver Reborn Advisory" description="Contact Driver Reborn Advisory for general questions, media inquiries, or partnerships." />
      <section className="section">
        <div className="container-narrow grid md:grid-cols-2 gap-10">
          <div>
            <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-2">Contact</div>
            <h1 className="font-bebas text-5xl sm:text-6xl uppercase leading-none mb-4" data-testid="contact-title">
              Talk to Driver Reborn Advisory.
            </h1>
            <p className="text-lg text-[#A1A1AA] leading-relaxed mb-4">
              For general questions, media, or partnerships, use the form and we will respond directly.
            </p>
            <p className="text-[#E6E6E6] leading-relaxed mb-6">
              Fleet owners: request a 20-minute conversation so Brandon can understand what is happening behind the trucks and identify the correct next step.
            </p>
            <BookingLink className="btn-primary mb-8" testid="contact-conversation-cta">
              {PRIMARY_CTA} <ArrowRight size={20}/>
            </BookingLink>
            <div className="mt-8 flex items-center gap-3 text-sm text-[#A1A1AA]">
              <Mail className="text-[#FF6A00]" size={18}/>
              <span>This contact form routes directly to Brandon.</span>
            </div>
          </div>
          <div>
            {state.done ? (
              <div className="border border-[#FF6A00] bg-[#111111] p-6" data-testid="contact-done">
                <h3 className="font-bebas text-3xl uppercase mb-2 text-[#FF6A00]">Message sent.</h3>
                <p className="text-[#A1A1AA]">Brandon replies within one business day.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <TF label="Name" value={form.name} onChange={v=>setForm({...form, name:v})} required testid="contact-name"/>
                <TF label="Email" type="email" value={form.email} onChange={v=>setForm({...form, email:v})} required testid="contact-email"/>
                <TF label="Phone (optional)" value={form.phone} onChange={v=>setForm({...form, phone:v})} testid="contact-phone"/>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">Message</label>
                  <textarea rows={5} required value={form.message} onChange={e=>setForm({...form, message:e.target.value})}
                    data-testid="contact-message"
                    className="w-full bg-[#2B2B2B] border border-[#2B2B2B] text-[#E6E6E6] px-4 py-3 focus:border-[#FF6A00] outline-none"/>
                </div>
                {state.error && <div className="text-[#EF4444] text-sm" data-testid="contact-error">{state.error}</div>}
                <button type="submit" disabled={state.loading} className="btn-primary w-full justify-center" data-testid="contact-submit">
                  {state.loading ? "Sending…" : <>Send Message <ArrowRight size={20}/></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}

function TF({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">{label}</label>
      <input type={type} value={value} onChange={e=>onChange(e.target.value)} required={required}
        data-testid={testid}
        className="w-full bg-[#2B2B2B] border border-[#2B2B2B] text-[#E6E6E6] px-4 py-3 focus:border-[#FF6A00] outline-none"/>
    </div>
  );
}
